import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectAPI } from '../services/api';
import { firmService } from '../services/firmService';
import { useAuth } from '../contexts/AuthContext';
import { Typography, Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, CircularProgress, Alert, Button, Breadcrumbs, Link } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

const Projects: React.FC = () => {
  const params = useParams<{ firmId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [projects, setProjects] = useState<any[]>([]);
  const [firmName, setFirmName] = useState<string>('');

  const isPmpAdminOrSiteAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some((role) => role.slug === 'pmp_admin' || role.slug === 'site_admin');
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await projectAPI.getAll(params.firmId ? { firm_id: params.firmId } : undefined);
        setProjects(res.data.data.data || res.data.data);
        if (params.firmId) {
          try {
            const firmRes = await firmService.getFirmById(parseInt(params.firmId));
            setFirmName(firmRes.data.name);
          } catch (_e) {
            setFirmName('');
          }
        } else {
          setFirmName('');
        }
      } catch (e: any) {
        setError(e.message || 'Failed to load projects');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [params.firmId]);

  if (loading) return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;

  return (
    <Box>
      {params.firmId && (
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            color="inherit"
            href="#"
            onClick={(e) => { e.preventDefault(); navigate('/firms'); }}
            sx={{ textDecoration: 'none' }}
          >
            Firms
          </Link>
          <Link
            color="inherit"
            href="#"
            onClick={(e) => { e.preventDefault(); navigate(`/firms/${params.firmId}`); }}
            sx={{ textDecoration: 'none' }}
          >
            {firmName || `Firm #${params.firmId}`}
          </Link>
          <Typography color="text.primary">Projects</Typography>
        </Breadcrumbs>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            {params.firmId ? 'Firm Projects' : 'Projects'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {params.firmId ? 'Projects scoped to the selected firm' : 'All projects'}
          </Typography>
        </Box>
        {params.firmId && isPmpAdminOrSiteAdmin() && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/firms/${params.firmId}/projects/new`)}
          >
            Create Project
          </Button>
        )}
      </Box>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Firm</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>#{p.id}</TableCell>
                  <TableCell>{p.name}</TableCell>
                  <TableCell>{p.firm?.name || p.firm_id}</TableCell>
                  <TableCell>
                    <Chip label={String(p.status).toUpperCase()} size="small" color={p.status === 'active' ? 'primary' : p.status === 'draft' ? 'default' : 'success'} />
                  </TableCell>
                  <TableCell>{new Date(p.created_at).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => navigate(`/firms/${p.firm_id || p.firm?.id || params.firmId}/projects/${p.id}`)}>View</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default Projects; 